/** @module DOM */

/**
 * Set's the focus on an element programmatically
 * @func
 * @param {Required<string[]>} selectors
 */
const focusDOMElement = (...selectors) => {
  if (!selectors || !selectors[0] ||
  /* SSR safe :) */
  typeof document === 'undefined') {
    return;
  }

  const selector = selectors.join(' ');
  /** @type {HTMLElement | null} */

  const el = document.querySelector(selector);

  if (el) {
    el.focus();
  }
};
/**
 * Appends a node to the body
 * @func
 * @param {Node} node
 */

const appendToBody = node => {
  if (typeof document === 'undefined') {
    return;
  }

  document.body.appendChild(node);
};
/**
 * Remove element from body
 * @func
 * @param {Node} node the node reference to delete
 */

const removeFromBody = node => {
  if (typeof document === 'undefined') {
    return;
  }

  document.body.removeChild(node);
};
/**
 * Appends a <br> to a node
 * @func
 * @param {Node} node
 */

const addLineBreak = (node, carriageCount = 1) => {
  // https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentHTML
  try {
    // @ts-ignore
    node.insertAdjacentHTML('beforeend', '<br>'.repeat(carriageCount)); // Add a line break!
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};
/**
 * Creates a hidden element
 * @func
 * @param {'div'} tag
 * @returns {Element}
 */

const createHiddenElement = (tag = 'div') => {
  const element = document.createElement(tag);
  element.setAttribute('aria-hidden', 'true');
  element.setAttribute('style', `
    position: fixed;
    opacity: 0;
    pointer-events: none;
  `);
  return element;
};

// @ts-check

/** @module Strings */

/**
 * Checks if value is a string (predicate functions!)
 * @func
 * @param {string} string
 * @returns {Boolean}
 */
const isString = string => {
  if (string === null || string === undefined) {
    return false;
  }

  return typeof string === 'string';
};
/**
 * Transforms the first letter to uppercase
 * Please, consider using CSS for this:
 * `.selector::first-letter { text-transform: uppercase }`
 * Uses `toUpperCase`
 * @func
 * @example
 * transformFirstLetterToUppercase('this is a sentence') // 'This is a sentence'
 * @param {String} str
 * @returns {String}
 */

const transformFirstLetterToUppercase = str => {
  if (!str) {
    return '';
  }

  const intermediateString = String(str);
  return intermediateString.charAt(0).toUpperCase() + intermediateString.slice(1);
};
/**
 * Transforms given string to title case
 * Please, consider using CSS for this: `.selector { text-transform: capitalize }`
 * @func
 * @example
 * toTitleCase('this is a sentence') // 'This Is A Sentence'
 * @param {String} str
 * @returns {String}
 */

const toTitleCase = str => {
  if (!str) {
    return '';
  }

  const intermediateString = String(str);
  const words = intermediateString.split(' ');
  return words.map(transformFirstLetterToUppercase).join(' ');
};
/**
 * Deletes trailing white spaces from string.
 * Alternative to modern .trimEnd()
 * Needed in Node 10
 * @func
 * @example
 * trimEnd('asdf ') // 'asdf'
 * @param {String} str
 * @returns {String}
 */

const trimEnd = str => {
  if (!str) {
    return '';
  }

  const intermediateString = String(str);
  const trimmedString = intermediateString.replace(/\s+$/, '');
  return trimmedString;
};

// @ts-check
/**
 * @func
 * @private
 * @param {Required<HTMLElement>} element
 */

const selectElementContents = element => {
  if (!element) {
    return;
  } // @ts-ignore


  if (element && element.select) {
    // @ts-ignore
    element.select();
    return;
  }

  window.getSelection().removeAllRanges();
  const range = document.createRange();
  range.selectNode(element);
  window.getSelection().addRange(range);
};
/**
 * Exec command on document.
 * @func
 * @private
 * @param {HTMLElement} el
 * @returns {boolean|Error}
 */


const execCommandCopy = el => {
  /**
   * @func
   * @param {ClipboardEvent} event
   */
  function listener(event) {
    event.clipboardData && event.clipboardData.setData('text/plain', el.innerText ||
    /** @type {HTMLTextAreaElement} */
    el.value);
    event.clipboardData && event.clipboardData.setData('text/html', el.innerHTML); // https://w3c.github.io/clipboard-apis/#override-copy

    event.preventDefault();
  }

  document.addEventListener('copy', listener);
  const isSuccessful = document.execCommand('copy');
  document.removeEventListener('copy', listener);
  return isSuccessful;
};
/**
 * @func
 * @private
 * @param {HTMLElement} container
 */


const setContainerStyles = container => {
  container.setAttribute('aria-hidden', 'true'); // do not set display none (won't work in Safari)

  container.setAttribute('style', `
    position: fixed;
    opacity: 0;
    pointer-events: none;
  `);
};
/**
 * @func
 * @private
 * @param {HTMLElement} element
 */


const isHTMLElement = element => element instanceof HTMLElement;
/**
 * Copy HTML element content to clipboard (plain and formatted)
 * @example
 * // you can pass a node reference
 * const node = document.getElementById(...)
 * copyToClipBoard(node, false).then(() => console.log('copied!'))
 * @example
 * // also accepts plain text
 * await copyToClipBoard('only text')
 * @func
 * @param {HTMLElement | string} content - Element to be copied, ca be an HTML element or a string
 * @param {Boolean} clearReferenceAfterwards
 * @returns {Promise<true> | never}
 */


const copyToClipBoard = (content, clearReferenceAfterwards = true) => {
  if (!content) {
    return Promise.reject(new Error('No element passed'));
  }

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Not in a browser environment'));
  } // @ts-ignore


  const isHTMLEl = isHTMLElement(content); // @ts-ignore

  const isStringContent = isString(content);

  if (!isHTMLEl && !isStringContent) {
    throw new Error('parameter is neither an HTML element nor a String');
  }

  const isCopySupported = document.queryCommandSupported('copy');

  if (!isCopySupported) {
    throw new Error('execCommand not supported');
  }

  return new Promise((resolve, reject) => {
    let container = document.createElement('div');

    if (isHTMLEl) {
      // @ts-ignore
      container.innerHTML = content.innerHTML;
    } else if (isStringContent) {
      container.innerText = String(content);
    }

    setContainerStyles(container);
    appendToBody(container); // @ts-ignore

    selectElementContents(container);
    const wasSuccessful = execCommandCopy(container);
    removeFromBody(container);

    if (clearReferenceAfterwards) {
      container = null; // clearing memory
    }

    if (!wasSuccessful) {
      reject(new Error('command "copy" failed'));
    } else {
      resolve(true);
    }
  });
};

/** @module Dates */

/**
 * Checks if provided date is "valid". Uses Date constructor + isNaN
 * @example
 * const a = isValidDate(new Date()) // true
 * const b = isValidDate('no') // false
 * @func
 * @param {string | Date} date date to be contrasted
 * @returns {Boolean}
 */
const isValidDate = date => {
  const d = new Date(date); // @ts-ignore

  return !isNaN(d);
};
/**
 * Gets the current date in the format of YYYYMMDDSS
 * Used as a naive UUID method
 * @example
 * const id = getCurrentDate()
 * @func
 * @returns {String}
 */

const getCurrentDate = () => {
  const d = new Date();
  const month = `${String(d.getMonth() + 1).padStart(2, '0')}`;
  const day = `${String(d.getDate()).padStart(2, '0')}`;
  const year = d.getFullYear();
  const seconds = d.getSeconds();
  return [year, month, day, seconds].join('');
};

// @ts-check

/** @module Emails */

/**
 * Simple email validation
 * @func
 * @param {string} email
 * @returns {boolean}
 */
const isValidEmail = email => {
  const regex = /^[_a-z0-9-+]+(\.[_a-z0-9-+]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,})$/i;
  return regex.test(email);
};

// @ts-check

/** @module Language */

/**
 * Asserts a condition. Otherwise, throws. Throws by default
 * (useful for required parameters in a function).
 * @example
 * const factory = ({ requiredParam = assertCondition('`requiredParam` is required') }) => { ... }
 * @func
 * @param {string} msg
 * @param {false} condition - something to match against
 */
const assertCondition = (msg, condition = false) => {
  if (!condition) {
    throw new Error(msg);
  }
};
/**
 * Executes a series of functions ("transformations") over the same type of stream.
 * Each function expects to recieve the same type the previous is returning
 * @func
 * @see https://github.com/graficos/pipe-js#intro
 * @param {Function[]} functions
 */

const pipe = (...functions) =>
/**
 * @param {any} value
 */
value => functions.reduce((currentValue, currentFunction) => currentFunction(currentValue), value);
/**
 * Same as `pipe` but wraps execution in Promises and returns a promise.
 * Provided functions can be async
 * @func
 * @see https://github.com/graficos/pipe-js#what-if-my-functions-are-asynchronous
 * @param {(() => any)[]} functions
 */

const asyncPipe = (...functions) =>
/** @func
* @param {any} value
* @returns {Promise<any>}
*/
value => functions.reduce(
/**
 * @param {Promise} currentPromise
 * @param {() => any} currentFunction
 */
(currentPromise, currentFunction) => currentPromise.then(currentFunction), Promise.resolve(value));

// @ts-check

/** @module Objects */

/**
 * @func
 * @private
 * @param {any} object
 * @returns {Boolean}
 */
const isFalsy
/* So, it's not an object */
= object => !object;
/**
 * Checks if an Object, Array or String is "empty"
 * @func
 * @example
 * isEmpty('') // true
 * isEmpty([]) // true
 * isEmpty({}) // true
 * isEmpty('a') // false
 * isEmpty(null) // true
 * isEmpty(['']) // false
 * isEmpty(['a']) // false
 * isEmpty({ a: '' }) // false
 * isEmpty(undefined) // true
 * @param {Object|Array|String} object
 * @returns {Boolean}
 */


const isEmpty = object => isFalsy(object) || !Object.keys(object).length;
/**
 * Same as `isObjectType` but more strict, not accepting any other type
 * than strictly Objects
 * @func
 * @example
 * strictIsObjectType({ a: true, b: '' }) // true
 * strictIsObjectType({}) // true
 *
 * strictIsObjectType([]) // false
 * strictIsObjectType('') // false
 * strictIsObjectType(3) // false
 * strictIsObjectType(NaN) // false
 * strictIsObjectType(undefined) // false
 * strictIsObjectType({}) // true
 * strictIsObjectType(Promise.resolve()) // false
 * strictIsObjectType(function*() {}) // false
 * strictIsObjectType(function() {}) // false
 * strictIsObjectType(new Map()) // false
 * @param {Object} object
 * @returns {Boolean}
 */

const strictIsObjectType = object => Object.prototype.toString.call(object) === '[object Object]';
/**
 * Same as `isEmpty` but only applied to object values (not arrays)
 * @func
 * @example
 * isEmptyObject({}) // true
 *
 * isEmptyObject(Promise.resolve()) // false
 * isEmptyObject(new Map()) // false
 * isEmptyObject({ a: true, b: '' }) // false
 * isEmptyObject([]) // false
 * isEmptyObject(3) // false
 * isEmptyObject(NaN) // false
 * isEmptyObject(undefined) // false
 * isEmptyObject(null) // false
 * isEmptyObject(function*() {}) // false
 * isEmptyObject(function() {}) // false
 * @param {Object} object
 * @returns {Boolean}
 */

const isEmptyObject = object => strictIsObjectType(object) && !Object.keys(object).length;
/**
 * typeof matches `object`
 * @func
 * @example
 * isObjectType({ a: true, b: '' }) // true
 * isObjectType([]) // true
 * isObjectType({}) // true
 * isObjectType(Promise.resolve()) // true
 * isObjectType(new Map()) // true
 *
 * isObjectType('') // false
 * isObjectType(3) // false
 * isObjectType(NaN) // false
 * isObjectType(undefined) // false
 * isObjectType(function*() {}) // false
 * isObjectType(function() {}) // false
 * @param {Object} object
 * @returns {Boolean}
 */

const isObjectType = object => !!(typeof object).match(/object/);
/**
 * All values are truthy. Works with arrays too.
 * @func
 * @example
 * allValuesAreTruthy({ a: true, b: true }) // true
 * allValuesAreTruthy({ a: 'a', b: 'a' }) // true
 * allValuesAreTruthy([true, true]) // true
 * allValuesAreTruthy({ a: false, b: 'asdf' }) // false
 * @param {{ [s: string]: any; } | ArrayLike<any>} object
 * @returns {Boolean}
 */

const allValuesAreTruthy = object => object && Object.values(object).length && Object.values(object).every(Boolean);
/**
 * Removes properties equal to undefined
 * but preserves empty strings, false and
 * other falsy values
 * @func
 * @example
 * deleteUndefinedProps({ a: undefined, b: true, c: 1, d: null })
 * // -> { b: true, c: 1, d: null }
 * @private
 * @param {Object} object
 * @returns {Object} object
 */

const deleteUndefinedProps = object => object && Object.keys(object).length && Object.keys(object).reduce((acc, curr) => ({ ...acc,
  ...(object[curr] !== undefined && {
    [curr]: object[curr]
  })
}), {}); // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value#Examples

const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (_key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return;
      }

      seen.add(value);
    }

    return value;
  };
};
/**
 * "DeepClones" an object but discarding circular references.
 * Uses JSON.parse.
 * @func
 * @example
 * const someObject = {a: 1, b: {c: 1}}
 *
 * deepClone(someObject) // {a: 1, b: {c: 1}}
 * @example
 * const someObject = {a: 1, b: {c: 1}}
 * const someObject.c = someObject // circular references too!
 * deepClone(someOtherObject) // {a: 1, b: {c: 1}, c: {a: 1, b: {c: 1}}}
 * @param {Object} object The object to be cloned
 * @returns A copied value representing a new reference and discarding circular references
 *
 * @todo Deprecate in favor of structuredClone in Node 18.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/structuredClone#browser_compatibility
 */


const deepClone = object => {
  if (isFalsy(object)) {
    return {};
  } // eslint-disable-next-line no-undef


  if (globalThis !== undefined && 'structuredClone' in globalThis) {
    // eslint-disable-next-line no-undef
    return globalThis.structuredClone(object);
  }

  if (!isObjectType(object)) {
    return object;
  }

  return JSON.parse(JSON.stringify(object, getCircularReplacer()));
};
/**
 * Alternative to lodash.get method
 * @func
 * @example
 * const obj = { a: ['asdf'] }
 * get(obj, 'a[0]') // 'asdf'
 * @example
 * const obj = { b: { c: 100 } }
 * get(obj, 'b.c') // 100
 * get(obj, 'b.nope', 'fallback') // 'fallback'
 * @param {Object|Array} object Object to inspect
 * @param {String} path string defining the path to visit.
 * @param {any} defaultValue fallback value in case the given path fails to retrieve nothing
 * @returns {any|undefined} undefined by default, in case the path does not exists
 */

const get = (object, path, defaultValue) => {
  var fullPath = path.replace(/\[/g, '.').replace(/["'\]]/g, '').split('.').filter(Boolean);
  return fullPath.every(everyFunc) ? object : defaultValue;

  function everyFunc(step) {
    return !(step && (object = object[step]) === undefined);
  }
};
/**
 * Not clever as some resolution engines I've used,
 * but it's meant to get either one or the next path passed as options.
 * Useful when you can get your data from different sources but you're
 * not sure what shape one you're recieving.
 * @func
 * @example
 * const obj = {
 *   a: 'asdf'
 * }
 * getSameOrSimilarPath(obj, ['nope', 'noope', 'a']) // 'asdf'
 * //                                          ^^^ good one
 * @example
 * // You can also provide a fallback
 * const obj = {
 *   a: 'asdf'
 * }
 * getSameOrSimilarPath(obj, ['nope', 'no, this one doesn\'t exists'], 'hehe') // 'hehe'
 * @param {Object} object The object to be visited
 * @param {Array<string>} options The posible paths
 * @param {unknown} defaultValue The fallback value
 */

const getSameOrSimilarPath = (object, options = [], defaultValue = undefined) => {
  if (!isObjectType(object)) {
    return defaultValue;
  }

  if (!options.length || isEmpty(object)) {
    return defaultValue;
  }

  let accessed;

  for (const option of options) {
    accessed = get(object, String(option), defaultValue); // finishes loop iteration earlier (because we end up the function execution)

    if (accessed) {
      return accessed;
    }
  }

  return accessed;
};
/**
 * Deep merge two objects. Note that this function will not
 * merge arrays.
 * @func
 * @example
 * const sourceA = {
 *   a: {
 *     b: [1],
 *   },
 * }
 * const sourceB = {
 *   a: {
 *     c: {},
 *     b: 1,
 *   },
 * }
 * deepMerge(sourceA, sourceB)
 * // ->
 * // {
 * //   a: {
 * //     b: 1, // note sourceB option remained
 * //     c: {},
 * //   },
 * // }
 * @param target
 * @param {...any} sources
 */

function deepMerge(target, ...sources) {
  if (!target) {
    return {};
  }

  if (!sources.length) {
    return target;
  }

  const source = sources.shift();

  if (strictIsObjectType(target) && strictIsObjectType(source)) {
    for (const key in source) {
      if (strictIsObjectType(source[key])) {
        if (!target[key]) {
          Object.assign(target, {
            [key]: {}
          });
        }

        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, {
          [key]: source[key]
        });
      }
    }
  }

  return deepMerge(target, ...sources);
}

/** @module UUID */

/**
 * Generates a random string
 * @func
 * @returns {string}
 * @category uuid
 */
const uuid = () => String(new Date().getTime() + Math.random());

/** @module Iterables */
const _isArrayEntriesNumberValuePairs = entriesToArray => // entries() method returns [[1, something], [2, something], ...]
!!(entriesToArray && entriesToArray.length && entriesToArray.every(pair => pair.length === 2) && // key-value pairs
entriesToArray.every(([firstElement]) => typeof firstElement === 'number'));

/**
 * Removes duplicates of an array. Uses Set, so Array of Objects can be used too,
 * it will remove repeated references (not values).
 *
 * @example
 * getUniqueList(...[2, 2]) // [2]
 * @func
 * @param  {...any} spreadCollection Note the "spread" operator. `spreadCollection` is an iterable. Do not pass an array directly, spread it! 🧈
 * @returns {Array<any>}
 */
const getUniqueList = (...spreadCollection) => {
  let array = [...(spreadCollection || [])];

  if (!array.length) {
    return [];
  }

  if (_isArrayEntriesNumberValuePairs(array)) {
    array = array.map(pair => pair[1]);
  }

  return [...new Set(array)];
};
/**
 * Resembles Python's range function.
 * Returns a list of numbers in the selected range.
 * Optionally, you can pass a step to the function
 * indicating the increment (or decrement) pace.
 *
 * **Note**: it is not-inclusive in the "right" bound.
 * Because if you do `range(10)`, it returns an array and it
 * makes more sense that such array starts with 0 and is of size `10`, not `11`.
 *
 * @see https://docs.python.org/3/library/functions.html#func-range
 * @see https://docs.python.org/3/library/stdtypes.html#ranges
 * @see https://stackoverflow.com/questions/4504662/why-does-rangestart-end-not-include-end
 * @see https://www.cs.utexas.edu/users/EWD/ewd08xx/EWD831.PDF
 *
 * @func
 * @param {number} [start] - start of the sequence. If a stop argument is not passed, it acts as the length of the new Array
 * @param {number} [stop]- start item value
 * @param {1} [steps] - increment ammunt
 * @returns {Array<number>}
 *
 * @example
 * expect(range(5)).toEqual([0, 1, 2, 3, 4])
 * expect(range(1, 5)).toEqual([1, 2, 3, 4])
 *
 * // more common usage:
 * range(5) // [0, 1, 2, 3, 4]
 *
 * // negative start (and stop) are allowed too
 * range(-1, 5) // [-1, 0, 1, 2, 3, 4]
 *
 * // you can also pass a step option to make the range increment by a given value
 * range(0, 1, .25) // [0, .25, .5, .75])
 */

const range = (start = 0, stop = 0, steps = 1) => {
  // works when neither of the range arguments are passed or are 0
  if (!start && !stop) {
    return [];
  }

  const hasStop = stop !== undefined || stop !== null; // steps cannot be zero for obvious reasons

  if (steps === 0) {
    throw new Error('step argument must not be zero');
  } // make it work if only one value was passed


  if (start === stop || !hasStop) {
    stop = start;
    start = 0;

    if (stop === 0) {
      return [];
    }
  } // flip start and stop to prevent infinite loop


  if (start > stop && Math.sign(steps) === 1) {
    const oldStart = start;
    start = stop;
    stop = oldStart;
  } else if (start < stop && Math.sign(steps) === -1) {
    steps = Math.abs(steps);
  } // catches cases when start is nullish (or zero)


  if (!start) {
    start = 0;
  }

  const result = [];

  if (steps > 0) {
    for (let i = start; i < stop; i += steps) {
      result.push(i);
    }
  } else if (steps < 0) {
    for (let i = start; i > stop; i += steps) {
      result.push(i);
    }
  }

  return result;
};

/** @module URLs */

/**
 * Get the current subdomain
 * Soon to be deprecated
 * @func
 * @deprecated
 * @param {string} host - defaults to `window.location.hostname`
 */
const getCurrentSubdomain = (host = window.location.hostname) => host.split('.')[0];
/**
 * Returns true if the given routeName and subdomain match
 * @func
 * @param {String} routeName - A vue router's $route.fullPath string
 * @param {String} subdomain - A subdomain/filter value (from the SearchBox
 * dropdown)
 * @return {Boolean} true if routeName and categoryLabel belong to the same
 * subdomain
 */

const sameSubdomain = (routeName, subdomain) => routeName.toLowerCase().includes(subdomain.toLowerCase());
/**
 * URL validation predicate function.
 * @see https://regex101.com/r/YH5nfc/2
 * @func
 * @param {String} string - the URL to validate
 * @return {Boolean}
 * @var {[type]}
 */

const isUrl = string => {
  const match = string && String(string).match( // eslint-disable-next-line
  /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?([\w\.\-]\/?)+|(?:www\.|[\-;:&=\+\$,\w]+@)[\w\.\-])((?:\/[\+~%\/\.\w\-]+)?\??(?:[\-\+=&;%@\.\w\/:\?]+)#?(?:[\.\!\/\\\w:\*%&=\-\?]+))?)/);
  return !!match && match[0].length === string.length;
};

/** @module YDR */
/** @typedef {('www'|'examples'|'grammar'|'quotes'|'reference'|'sentence'|'spanish'|'thesaurus'|'websters'|'wordfinder'|'www'|'es'|'abbreviations'|'esl'|'education')} Subdomain */

/** @typedef {'https:'|'http:'} Protocol */

/** @type {Protocol} */

const PROTOCOL = 'https:';
const DOMAIN = '.yourdictionary.com';
/**
 * Valid YourDictionary.com subdomains
 * @type {{[string]: Subdomain}}
 */

const subdomains = {
  DEFINITIONS: 'www',
  EXAMPLES: 'examples',
  GRAMMAR: 'grammar',
  QUOTES: 'quotes',
  REFERENCE: 'reference',
  SENTENCES: 'sentence',
  SPANISH: 'spanish',
  THESAURUS: 'thesaurus',
  WEBSTERS: 'websters',
  WORDFINDER: 'wordfinder',
  WWW: 'www',
  ES: 'es',
  ABBREVIATIONS: 'abbreviations',
  ESL: 'esl',
  EDUCATION: 'education'
};
/**
 * @func
 * @param {Protocol} protocol - either http or https (+ ':')
 * @param {String} domain - full domain, including env (optional) and port (optional).  Defaults to '.yourdictionary.com'
 */

const getRoot = (protocol = PROTOCOL, domain = DOMAIN) => ({
  EXAMPLES: `${protocol}//${subdomains.EXAMPLES}${domain}`,
  GRAMMAR: `${protocol}//${subdomains.GRAMMAR}${domain}`,
  QUOTES: `${protocol}//${subdomains.QUOTES}${domain}`,
  REFERENCE: `${protocol}//${subdomains.REFERENCE}${domain}`,
  SENTENCES: `${protocol}//${subdomains.SENTENCES}${domain}`,
  SPANISH: `${protocol}//${subdomains.SPANISH}${domain}`,
  THESAURUS: `${protocol}//${subdomains.THESAURUS}${domain}`,
  WEBSTERS: `${protocol}//${subdomains.WEBSTERS}${domain}`,
  WWW: `${protocol}//${subdomains.WWW}${domain}`,
  WORDFINDER: `https://wordfinder.yourdictionary.com`,
  ES: `${protocol}//${subdomains.ES}${domain}`,
  ABBREVIATIONS: `${protocol}//${subdomains.ABBREVIATIONS}${domain}`,
  ESL: `${protocol}//${subdomains.ESL}${domain}`,
  EDUCATION: `${protocol}//${subdomains.EDUCATION}${domain}`
});
/**
 * @func
 * @private
 * @param {Subdomain} subdomain
 * @param {Protocol} protocol - either http or https (+ ':')
 * @param {String} domain - full domain, including env (optional) and port (optional). Defaults to '.yourdictionary.com'
 */

const getSubdomainRootURL = ({
  protocol,
  subdomain,
  domain
} = {}) => {
  subdomain = Object.values(subdomains).includes(subdomain) ? subdomain : subdomains.WWW;
  const key = Object.keys(subdomains).find(key => subdomains[key] === subdomain);
  return getRoot(protocol, domain)[key] || getRoot(protocol, domain).WWW;
};
/**
 * Replaces the current location to navigate to the given path. This function is
 * used to force a programatic navigation and Vue Router cannot be used because
 * client routers cannot change the origin (See history.pushState)
 * @see https://developer.mozilla.org/en-US/docs/Web/API/History/pushState
 * @func
 * @param {Protocol} protocol - either http or https (+ ':')
 * @param {Subdomain} subdomain - A valid YDR subdomain.
 * @param {String} domain - full domain, including env (optional) and port (optional). Defaults to '.yourdictionary.com'
 * @param {String} path - A relative URL from subdomains root. MUST have a leading "/"
 */


const navigateTo = (protocol = PROTOCOL, subdomain = assertCondition('domain is mandatory'), domain = DOMAIN, path = '') => {
  if (domain[0] !== '.') {
    throw new Error('"domain" must start with a dot');
  }

  const url = getSubdomainRootURL({
    protocol,
    subdomain,
    domain
  }) + path;
  window.location.assign(url);
};
/**
 * Return true if window's width is less than the threshold. used for Ads also.
 * @func
 * @param {number} SMALL_DEVICE_WIDTH - breakpoint constant
 * @return {Boolean} true if window's width is of a small device
 */

const isSmallDevice = (SMALL_DEVICE_WIDTH = 768) => window.innerWidth < SMALL_DEVICE_WIDTH;
/**
 * Does a POST request to YDR v1 /search endpoint (this will perform the search
 * and redirect the user to the results page).
 * @func
 * @param {String} subdomain - A valid YDR subdomain.
 * @param {String} term - A search term valid
 */

const legacyPostSearch = (subdomain, term) => {
  const url = `https://${subdomain}.yourdictionary.com/search/`;
  const form = document.createElement('form');
  form.setAttribute('method', 'POST');
  form.setAttribute('action', url);
  const input = document.createElement('input');
  input.setAttribute('type', 'hidden');
  input.setAttribute('name', 'ydQ');
  input.setAttribute('value', term);
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
};

// @ts-check

/** @module Async */

/**
 * Delay (AKA "sleep") function execution a given
 * ammount of milliseconds without blocking next calls
 * @func
 * @param {number} milliseconds
 *
 * @example
 * await delay(2000).then(() => console.log('Hello world, after 2 seconds!'))
 *
 * @returns {Promise<void>}
 */
const delay = milliseconds => new Promise(resolve => {
  setTimeout(resolve, milliseconds);
});
/**
 * Ensures a function is only called at most once during
 * a given window of time. Minimum time between calls is
 * a full cycle in the Event Loop. Like a naive `nextTick`.
 * @func
 * @param {Function} fn
 * @param {number} limit milliseconds
 * @returns {Function}
 *
 * @see https://codeburst.io/throttling-and-debouncing-in-javascript-b01cad5c8edf
 * @example
 * const updateState = () => {/* ... *\/}
 * throttle(updateState(args))() // `updateState` won't be called again at least in `limit` milliseconds
 */

const throttle = (fn, limit = 0) => {
  var waiting = false;
  return function () {
    if (!waiting) {
      fn.apply(this, arguments);
      waiting = true;
      setTimeout(function () {
        waiting = false;
      }, limit);
    }
  };
};
/**
 * Debounce a Function:
 * Unlike throttle where the callback is called immediately,
 * debounce ensures that a function is not called until
 * the specified time has passed. If no `timeout` is passed,
 * the ellapsed time is 1 event loop cycle
 * because the setTimeout method is used internally.
 *
 * @example
 * const debouncedExpensiveFunction = debounce(() => {...}), 350)
 *
 * @example
 * const debouncedExpensiveFunction = debounce(expensiveFunction, 350)
 * debouncedExpensiveFunction()
 *
 * @func
 * @param {Function} fn function to be debounced
 * @param {0} [timeout] delay time
 * @returns {() => unknown}
 */

const debounce = (fn, timeout = 0) => {
  let timerId;
  return (...args) => {
    timerId && clearTimeout(timerId);
    timerId = setTimeout(() => {
      fn(...args);
    }, timeout);
  };
};

/** @module Images */

/**
 * Cheks if a given URL for an image is available over the net.
 * It uses a simple DOM trick to test it.
 * Be careful as <img src> can be an XSS attack
 * vector.
 * @see https://owasp.org/www-community/xss-filter-evasion-cheatsheet
 *
 * @example
 * import { imageExists } from 'img-exists'
 *  const src = "https://..."
 *
 *  imageExists(src)
 *    .then(({ img }) => {
 *      document.body.appendChild(img) // or whatever
 *    })
 *    .catch(({ error }) => console.log(error))
 * @func
 * @param  {String} src 'https://...'
 * @returns {{ img: HTMLImageElement } | { error: Error | string }}
 */
const imageExists = src => {
  if (!src) {
    return Promise.reject({
      error: 'no src passed'
    });
  }

  if (!/^http/.test(src)) {
    return Promise.reject({
      error: 'only URLs starting with http are valid'
    });
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve({
      img
    });

    img.onerror = error => reject({
      error
    });

    img.src = src; // requests the image asynchronously
  });
};

// @ts-check

/** @module Maths */

/**
 * Returns a number whose value is limited to the given range.
 * If the value was higher or lower, the upper or lower limit will be returned.
 *
 * @func
 * @param {number} num
 * @param {number} min
 * @param {number} max
 * @return {number} - number between min and max or min or max
 *
 * @see https://www.webtips.dev/webtips/javascript/how-to-clamp-numbers-in-javascript
 *
 * @notes
 * There a proposal (currently on Stage 1) for a standard clamp function.
 * https://github.com/rwaldron/proposal-math-extensions
 * @example
 * clamp(1, 0, 10); // => 1
 * clamp(500, 0, 255); // => 255
 */
const clamp = (num, min, max) => // Alternative implementation: Math.min(max, Math.max(min, num))
num < min ? min : num > max ? max : num;
/**
 * Returns a random number between min and max.
 * @func
 * @param {number} min
 * @param {number} max
 * @return {number} - random number between min and max
 *
 * @example
 * random(1, 10); // => 5.5648723218098
 */

const random = (min, max) => Math.random() * (max - min) + min;
/**
 * Returns a random integer number between min and max.
 * Same as `random` but returns integer numbers.
 *
 * It uses `Math.round()` to round the result. So, the nearest integer is returned.
 *
 * @func
 * @param {number} min
 * @param {number} max
 * @return {number} - random number between min and max
 *
 * @example
 * random(1, 10); // => 5
 */

const randomInt = (min, max) => Math.round(random(min, max));
/**
 * Returns a number whose value is limited to the given range, just like `clamp`.
 * The difference is that `clamp` doesn't return the lower or upper limit if
 * the value is outside the range. Instead, `wrap` returns the value wrapped
 * around the range.
 *
 * @func
 * @param {number} num
 * @param {number} min
 * @param {number} max
 * @return {number} - wrapped number between min and max
 *
 * @example
 * wrap(1, 0, 10); // => 1
 * wrap(500, 0, 255); // => 245 // 500 % 255 = 245
 */

const wrap = (num, min, max) => {
  const rangeSize = max - min;
  return ((num - min) % rangeSize + rangeSize) % rangeSize + min;
};

export { addLineBreak, allValuesAreTruthy, appendToBody, assertCondition, asyncPipe, clamp, copyToClipBoard, createHiddenElement, debounce, deepClone, deepMerge, delay, deleteUndefinedProps, focusDOMElement, get, getCurrentDate, getCurrentSubdomain, getRoot, getSameOrSimilarPath, getUniqueList, imageExists, isEmpty, isEmptyObject, isObjectType, isSmallDevice, isString, isUrl, isValidDate, isValidEmail, legacyPostSearch, navigateTo, pipe, random, randomInt, range, removeFromBody, sameSubdomain, strictIsObjectType, subdomains, throttle, toTitleCase, trimEnd, uuid, wrap };
//# sourceMappingURL=index.mjs.map
